import React, { useContext, useState, useEffect } from "react";
import { Button, Divider, Menu, MenuItem } from "@mui/material";
import { MuiBox, MuiTypography } from "components/atoms";
import { useTranslation } from "react-i18next";

// SVGs
import ArrowDownContained from "assets/images/svg-icons/chevrondowncontained.svg";
import ArrowUpContained from "assets/images/svg-icons/chevronUpcontained.svg";
import CheckTick from "assets/images/svg-icons/blueRightTick.svg";
import { FilterContext } from "context/FilterContext";
import { updateSortOrder } from "store/slices/folders";
import { useDispatch } from "react-redux";
import { updateSortOrderInGeneral } from "store/slices/general";

const SortMenu = ({ sortModal, setSortModal }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { filterData, setFilterData } = useContext(FilterContext);

  const [anchorEl, setAnchorEl] = useState(null);

  // ─── Helpers to keep UI ↔ Redux values in sync ───────────────────────────
  const isDateSort = (sortBy) =>
    sortBy === "date_modified" ||
    sortBy === "date_modified_by_me" ||
    sortBy === "date_opened_by_me";

  // FilterContext holds UI values ("newest"/"oldest"/"asc"/"desc") while Redux
  // holds "asc"/"desc". Both flow through these helpers, so treat either
  // descending form ("desc" | "newest") as descending.
  const isDescending = (order) => order === "desc" || order === "newest";

  // Convert any stored order (UI or Redux form) to the value the menu expects.
  const toUiSortOrder = (sortBy, order) => {
    const desc = isDescending(order);
    if (isDateSort(sortBy)) {
      return desc ? "newest" : "oldest";
    }
    return desc ? "desc" : "asc";
  };

  // Convert the value the user clicked into the Redux form.
  const toReduxSortOrder = (value) => (isDescending(value) ? "desc" : "asc");

  // ─── Local UI state (always holds the display values) ────────────────────
  const [selectedItem, setSelectedItem] = useState(
    filterData?.sortBy || "name",
  );
  const [sorting, setSorting] = useState(
    toUiSortOrder(
      filterData?.sortBy || "name",
      filterData?.sortOrder || "asc",
    ),
  );

  // Keep local state in sync when filterData changes from outside
  useEffect(() => {
    const currentSortBy = filterData?.sortBy || "name";
    const currentOrder = filterData?.sortOrder || "asc";

    setSelectedItem(currentSortBy);
    setSorting(toUiSortOrder(currentSortBy, currentOrder));
  }, [filterData?.sortBy, filterData?.sortOrder]);

  const menuItems = [
    { label: "modal.filter.name", value: "name" },
    { label: "common.dateModified", value: "date_modified" },
    { label: "filter.dateModifiedByMe", value: "date_modified_by_me" },
    { label: "filter.dateOpenedByMe", value: "date_opened_by_me" },
  ];

  const sortingOptions = [
    { label: "filter.aToZ", value: "asc" },
    { label: "filter.zToA", value: "desc" },
  ];

  const dateSortingOptions = [
    { label: "filter.newToOld", value: "newest" },
    { label: "filter.oldToNew", value: "oldest" },
  ];

  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (value) => {
    // Default order for the newly selected column
    const newUiSortOrder = value === "name" ? "asc" : "newest";
    const reduxSortOrder = toReduxSortOrder(newUiSortOrder);

    // 1. Update FilterContext (keeps the UI value)
    setFilterData({
      ...filterData,
      sortBy: value,
      sortOrder: newUiSortOrder,
    });

    // 2. Update local UI state
    setSelectedItem(value);
    setSorting(newUiSortOrder);

    // 3. Update the page-level sortModal (still holds the UI value)
    if (setSortModal && sortModal) {
      setSortModal({
        ...sortModal,
        sortBy: value,
        sortOrder: newUiSortOrder,
      });
    }

    // 4. Persist normalised values in Redux
    dispatch(
      updateSortOrder({
        sortBy: value,
        sortOrder: reduxSortOrder,
      }),
    );
    dispatch(
      updateSortOrderInGeneral({
        sortBy: value,
        sortOrder: reduxSortOrder,
      }),
    );

    handleClose();
  };

  const handleClickSorting = (value) => {
    const currentSortBy = selectedItem || filterData?.sortBy || "name";
    const reduxSortOrder = toReduxSortOrder(value);

    // 1. Update FilterContext (UI value)
    setFilterData({
      ...filterData,
      sortOrder: value,
    });

    // 2. Update local UI state
    setSorting(value);

    // 3. Update the page-level sortModal
    if (setSortModal && sortModal) {
      setSortModal({
        ...sortModal,
        sortOrder: value,
      });
    }

    // 4. Persist normalised values in Redux
    dispatch(
      updateSortOrder({
        sortBy: currentSortBy,
        sortOrder: reduxSortOrder,
      }),
    );
    dispatch(
      updateSortOrderInGeneral({
        sortBy: currentSortBy,
        sortOrder: reduxSortOrder,
      }),
    );

    handleClose();
  };

  return (
    <MuiBox sx={{ width: "auto" }}>
      <Button
        variant="text"
        endIcon={
          <MuiBox sx={{ height: "22px", width: "22px" }}>
            <img
              src={
                sorting === "asc" || sorting === "oldest"
                  ? ArrowDownContained
                  : ArrowUpContained
              }
              alt=""
              className="img-full"
            />
          </MuiBox>
        }
        sx={{
          color: "text.darkBlack",
          borderRadius: "8px",
          fontSize: "18px",
          fontWeight: 600,
          "&:hover": {
            backgroundColor: "transparent",
          },
        }}
        onClick={handleClick}
      >
        {t(menuItems?.find((item) => item?.value === selectedItem)?.label)}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          disablePadding: true,
        }}
        slotProps={{
          paper: {
            sx: {
              mt: "8px",
              p: "8px",
              boxShadow: "0px 6px 24px 0px #0000000D",
              borderRadius: "8px",
              border: "1px solid",
              borderColor: "background.gray400",
            },
          },
        }}
      >
        {menuItems.map((item) => (
          <MenuItem
            key={item?.value}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              borderRadius: "8px",
            }}
            onClick={() => handleMenuItemClick(item?.value)}
          >
            <MuiBox sx={{ height: "20px", width: "20px", display: "flex" }}>
              {selectedItem === item?.value && (
                <img src={CheckTick} alt="selected" className="img-full" />
              )}
            </MuiBox>
            <MuiTypography fontWeight="500" variant="14" color="text.darkBlack">
              {t(item?.label)}
            </MuiTypography>
          </MenuItem>
        ))}

        <Divider sx={{ width: "100%" }} />

        {selectedItem === "name"
          ? sortingOptions.map((item) => (
            <MenuItem
              key={item?.value}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                borderRadius: "8px",
              }}
              onClick={() => handleClickSorting(item?.value)}
            >
              <MuiBox sx={{ height: "20px", width: "20px", display: "flex" }}>
                {sorting === item?.value && (
                  <img src={CheckTick} alt="selected" className="img-full" />
                )}
              </MuiBox>
              <MuiTypography
                fontWeight="500"
                variant="14"
                color="text.darkBlack"
              >
                {t(item?.label)}
              </MuiTypography>
            </MenuItem>
          ))
          : dateSortingOptions.map((item) => (
            <MenuItem
              key={item?.value}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                borderRadius: "8px",
              }}
              onClick={() => handleClickSorting(item?.value)}
            >
              <MuiBox sx={{ height: "20px", width: "20px", display: "flex" }}>
                {sorting === item?.value && (
                  <img src={CheckTick} alt="selected" className="img-full" />
                )}
              </MuiBox>
              <MuiTypography
                fontWeight="500"
                variant="14"
                color="text.darkBlack"
              >
                {t(item?.label)}
              </MuiTypography>
            </MenuItem>
          ))}
      </Menu>
    </MuiBox>
  );
};

export default SortMenu;